/**
 * Depois da AUTORIZAÇÃO: abrir o DANFE e baixar o PDF e o XML autorizado. Só o navegador
 * participa (os arquivos já estão no sistema, gravados pelo worker); nada aqui fala com o
 * provedor nem muda a nota.
 *
 * Downloads (correção da homologação — "cliquei e nada baixou"): os arquivos são PREPARADOS
 * uma vez (baixados da API com o login, conferidos e montados também em ZIP) e cada botão é um
 * LINK DIRETO para o arquivo já pronto. O clique da pessoa vai ao link: o navegador trata como
 * ação do usuário. Antes, o arquivo era buscado depois do clique e o download disparado por
 * código; somado ao 2º download automático (sem clique), o Chrome pode bloquear os downloads
 * do endereço ("vários downloads") sem nenhum aviso na tela.
 *
 * Automático (preferência): abre o DANFE na guia preparada e faz UM download só — o ZIP com
 * PDF e XML — para não cair no bloqueio de vários downloads.
 *
 * Bloqueio de pop-up: o navegador só deixa abrir guia durante um clique. Por isso a guia é
 * PREPARADA no clique de "Finalizar" e mostra as etapas reais da emissão (atualizadas pela
 * tela de resultado) até a autorização. O DANFE nunca aparece antes da autorização. Só o
 * DANFE em PDF abre em guia: um DANFE em HTML é apenas baixado.
 */
import type { FiscalDocumentDetail } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { apiFetchBlob, errorMessage, saveBlob } from "@/lib/api";
import {
  elapsedLabel,
  fiscalFileName,
  looksLikePdf,
  looksLikeXml,
  makeZip,
  type EmissionStage,
  type TimelineStep,
} from "./emission-lib";

// ------------------------------------------------------------------ guia preparada
const COLORS = {
  page: "#FAF8F4",
  text: "#382115",
  muted: "#6b5546",
  card: "#FFFFFF",
  border: "#E7DED3",
  caramel: "#B66A35",
  ok: "#2F7D4F",
  warn: "#9A5B00",
  error: "#B42318",
};

const STATE_TEXT: Record<TimelineStep["state"], string> = {
  done: "concluída",
  current: "em andamento",
  pending: "a seguir",
  warn: "atenção",
  error: "não concluída",
};

export type TabView = {
  title: string;
  detail: string;
  steps?: TimelineStep[];
  /** tempo desde o "Finalizar" */
  elapsedMs?: number;
  tone?: EmissionStage["tone"];
  /** indicador girando: ainda vai mudar sozinha */
  busy?: boolean;
};

/**
 * Monta o conteúdo da guia preparada SÓ com DOM e estilos via CSSOM (nada de HTML vindo de
 * fora; funciona mesmo com política de segurança de conteúdo restritiva).
 */
export function renderTab(tab: Window, view: TabView): void {
  try {
    const doc = tab.document;
    doc.title = view.title;
    const el = <K extends keyof HTMLElementTagNameMap>(
      tag: K,
      style: Partial<CSSStyleDeclaration>,
      text?: string,
    ) => {
      const n = doc.createElement(tag);
      Object.assign(n.style, style);
      if (text !== undefined) n.textContent = text;
      return n;
    };
    Object.assign(doc.body.style, {
      margin: "0",
      minHeight: "100vh",
      background: COLORS.page,
      color: COLORS.text,
      fontFamily: "Inter, system-ui, sans-serif",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      boxSizing: "border-box",
    });
    const toneColor =
      view.tone === "ok"
        ? COLORS.ok
        : view.tone === "error"
          ? COLORS.error
          : view.tone === "warn"
            ? COLORS.warn
            : COLORS.caramel;
    const card = el("main", {
      width: "100%",
      maxWidth: "30rem",
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: "16px",
      padding: "24px",
      boxShadow: "0 1px 3px rgba(56,33,21,.08)",
    });
    const head = el("div", { display: "flex", gap: "12px", alignItems: "flex-start" });
    const icon = el("span", {
      flex: "0 0 auto",
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      marginTop: "2px",
      boxSizing: "border-box",
      border: `3px solid ${toneColor}`,
      ...(view.busy ? { borderTopColor: "transparent" } : { background: toneColor }),
    });
    icon.setAttribute("aria-hidden", "true");
    if (view.busy && typeof icon.animate === "function") {
      icon.animate([{ transform: "rotate(0deg)" }, { transform: "rotate(360deg)" }], {
        duration: 900,
        iterations: Infinity,
      });
    }
    const texts = el("div", { minWidth: "0" });
    texts.setAttribute("role", "status");
    texts.append(
      el("h1", { fontSize: "19px", lineHeight: "1.3", margin: "0 0 6px" }, view.title),
      el(
        "p",
        { fontSize: "15px", lineHeight: "1.5", color: COLORS.muted, margin: "0" },
        view.detail,
      ),
    );
    head.append(icon, texts);
    card.append(head);
    if (view.steps) {
      const list = el("ol", { listStyle: "none", padding: "0", margin: "20px 0 0" });
      list.setAttribute("aria-label", "Etapas da emissão");
      for (const s of view.steps) {
        const color =
          s.state === "done"
            ? COLORS.ok
            : s.state === "error"
              ? COLORS.error
              : s.state === "warn"
                ? COLORS.warn
                : s.state === "current"
                  ? COLORS.caramel
                  : COLORS.border;
        const li = el("li", {
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "7px 0",
          fontSize: "15px",
          color: s.state === "pending" ? COLORS.muted : COLORS.text,
          fontWeight: s.state === "pending" || s.state === "done" ? "400" : "600",
        });
        li.dataset["state"] = s.state;
        const dot = el(
          "span",
          {
            flex: "0 0 auto",
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            border: `2px solid ${color}`,
            background: s.state === "done" ? color : "transparent",
            color: s.state === "done" ? "#fff" : color,
            fontSize: "13px",
            lineHeight: "18px",
            textAlign: "center",
            boxSizing: "border-box",
          },
          s.state === "done" ? "✓" : s.state === "error" || s.state === "warn" ? "!" : "",
        );
        dot.setAttribute("aria-hidden", "true");
        const sr = el(
          "span",
          {
            position: "absolute",
            width: "1px",
            height: "1px",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
          },
          ` (${STATE_TEXT[s.state]})`,
        );
        li.append(dot, el("span", {}, s.label), sr);
        list.append(li);
      }
      card.append(list);
    }
    if (view.elapsedMs !== undefined) {
      card.append(
        el(
          "p",
          { fontSize: "13px", lineHeight: "1.5", color: COLORS.muted, margin: "16px 0 0" },
          `Tempo desde a finalização: ${elapsedLabel(view.elapsedMs)}. A emissão continua no servidor mesmo se esta guia for fechada.`,
        ),
      );
    }
    doc.body.replaceChildren(card);
  } catch {
    /* guia já navegou ou foi fechada */
  }
}

/**
 * Prepara a guia do DANFE durante o clique (única forma de não ser bloqueada).
 * Devolve `null` se o navegador bloqueou.
 */
export function preparePendingTab(): Window | null {
  let tab: Window | null = null;
  try {
    tab = window.open("", "_blank");
  } catch {
    tab = null;
  }
  if (!tab) return null;
  renderTab(tab, {
    title: "Finalizando a venda…",
    detail:
      "Conferindo a nota e confirmando a venda. As etapas da emissão aparecem aqui e o DANFE abre nesta guia assim que a SEFAZ autorizar. Você pode voltar à guia do CaféGestor.",
    busy: true,
  });
  return tab;
}

export function closeTab(tab: Window | null): void {
  try {
    if (tab && !tab.closed) tab.close();
  } catch {
    /* nada a fazer */
  }
}

/** Mostra o PDF na guia (preparada ou nova). Devolve false se o navegador bloqueou. */
export function showPdf(blob: Blob, tab: Window | null): boolean {
  const url = URL.createObjectURL(blob);
  // a guia mantém o PDF aberto; o endereço temporário é liberado depois
  setTimeout(() => URL.revokeObjectURL(url), 10 * 60_000);
  try {
    if (tab && !tab.closed) {
      tab.location.href = url;
      return true;
    }
    const opened = window.open(url, "_blank");
    return opened !== null;
  } catch {
    return false;
  }
}

// ------------------------------------------------------------------ arquivos preparados
type FileRef = FiscalDocumentDetail["files"][number];
export function filesOf(d: Pick<FiscalDocumentDetail, "files">) {
  const danfe: FileRef | undefined =
    d.files.find((f) => f.kind === "danfe_pdf") ?? d.files.find((f) => f.kind === "danfe_html");
  const xml: FileRef | undefined = d.files.find((f) => f.kind === "xml");
  return { danfe, xml };
}

export type DocForFiles = Pick<
  FiscalDocumentDetail,
  "id" | "files" | "model" | "series" | "number" | "environment" | "status"
>;

export type PreparedFile = {
  kind: "danfe_pdf" | "danfe_html" | "xml" | "zip";
  name: string;
  blob: Blob;
};
export type PreparedFiles = {
  danfe: PreparedFile | null;
  xml: PreparedFile | null;
  /** DANFE + XML num arquivo só (quando os dois estão bons) */
  zip: PreparedFile | null;
  /** o que deu errado, em linguagem de quem usa (ausente, inválido, erro da API) */
  problems: string[];
};

const MIME: Record<PreparedFile["kind"], string> = {
  danfe_pdf: "application/pdf",
  danfe_html: "text/html",
  xml: "application/xml",
  zip: "application/zip",
};

/**
 * Baixa da API (com o login e a empresa ativa) o DANFE e o XML gravados pelo worker, confere
 * o conteúdo (um "PDF" que não é PDF, ou uma página de erro no lugar do XML, é apontado em vez
 * de oferecido) e monta o ZIP. Lê os bytes uma vez e monta tudo a partir deles. Nunca chama o
 * provedor.
 */
export async function prepareFiles(d: DocForFiles): Promise<PreparedFiles> {
  const { danfe, xml } = filesOf(d);
  const problems: string[] = [];
  const out: PreparedFiles = { danfe: null, xml: null, zip: null, problems };
  const bytesOf = async (f: FileRef, kind: PreparedFile["kind"], what: string) => {
    try {
      const { blob } = await apiFetchBlob(
        `/fiscal/documentos/${d.id}/arquivos/${f.id}`,
        fiscalFileName(d, kind),
      );
      return new Uint8Array(await blob.arrayBuffer());
    } catch (err) {
      problems.push(`${what}: não foi possível obter o arquivo (${errorMessage(err)}).`);
      return null;
    }
  };
  const file = (kind: PreparedFile["kind"], data: Uint8Array<ArrayBuffer>): PreparedFile => ({
    kind,
    name: fiscalFileName(d, kind),
    blob: new Blob([data], { type: MIME[kind] }),
  });
  let danfeBytes: Uint8Array<ArrayBuffer> | null = null;
  let xmlBytes: Uint8Array<ArrayBuffer> | null = null;
  if (danfe) {
    const kind = danfe.kind === "danfe_html" ? "danfe_html" : "danfe_pdf";
    const data = await bytesOf(danfe, kind, "DANFE");
    if (data && kind === "danfe_pdf" && !looksLikePdf(data.subarray(0, 8))) {
      problems.push(
        "DANFE: o arquivo recebido do provedor não é um PDF válido. Use “Consultar agora” na nota para buscá-lo de novo.",
      );
    } else if (data) {
      danfeBytes = data;
      out.danfe = file(kind, data);
    }
  } else {
    problems.push("DANFE: ainda não está no sistema.");
  }
  if (xml) {
    const data = await bytesOf(xml, "xml", "XML");
    if (data && !looksLikeXml(new TextDecoder().decode(data.subarray(0, 256)))) {
      problems.push("XML: o arquivo recebido do provedor não é um XML válido.");
    } else if (data) {
      xmlBytes = data;
      out.xml = file("xml", data);
    }
  } else {
    problems.push("XML: ainda não está no sistema.");
  }
  if (out.danfe && out.xml && danfeBytes && xmlBytes) {
    out.zip = file(
      "zip",
      makeZip([
        { name: out.danfe.name, data: danfeBytes },
        { name: out.xml.name, data: xmlBytes },
      ]),
    );
  }
  return out;
}

/**
 * Arquivos da nota prontos para abrir e baixar (um pedido à API por arquivo, compartilhado
 * entre o cartão da nota e a entrega automática). Só para nota autorizada com DANFE.
 */
export function usePreparedFiles(d: DocForFiles | undefined) {
  const ids = d ? d.files.map((f) => f.id).join(",") : "";
  const enabled = d !== undefined && d.status === "autorizada" && filesOf(d).danfe !== undefined;
  return useQuery({
    queryKey: ["fiscal-arquivos", d?.id ?? "-", ids],
    queryFn: () => prepareFiles(d!),
    enabled,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: 1,
  });
}

/** Endereço temporário (blob:) para o link; liberado quando o arquivo muda ou a tela sai. */
export function useObjectUrl(blob: Blob | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return url;
}

// ------------------------------------------------------------------ entrega automática
export type DeliveryState =
  | { phase: "idle" }
  | { phase: "waiting_files" }
  | { phase: "working" }
  | {
      phase: "done";
      /** null = abrir não foi pedido */
      opened: boolean | null;
      /** nome do arquivo baixado automaticamente (null = não pedido) */
      downloaded: string | null;
    }
  | { phase: "error"; message: string };

const deliveredKey = (id: string) => `cafegestor.nfe.entregue.${id}`;
function alreadyDelivered(id: string): boolean {
  try {
    return sessionStorage.getItem(deliveredKey(id)) === "1";
  } catch {
    return false;
  }
}
function markDelivered(id: string) {
  try {
    sessionStorage.setItem(deliveredKey(id), "1");
  } catch {
    /* sem armazenamento */
  }
}

type PreparedQuery = { data?: PreparedFiles | undefined; isError: boolean; error: unknown };

/**
 * Entrega automática, UMA vez por nota nesta sessão (recarregar a página não repete):
 * autorizada e com os arquivos prontos, abre o DANFE (na guia preparada, se houver) e faz UM
 * download — o ZIP com PDF e XML. Sem autorização, nada é aberto nem baixado (a guia mostra o
 * motivo: quem a atualiza é a tela de resultado).
 */
export function useAutoDelivery(
  d: DocForFiles | undefined,
  prefs: { openDanfe: boolean; download: boolean },
  pendingTab: Window | null,
  prepared: PreparedQuery,
): DeliveryState {
  const [state, setState] = useState<DeliveryState>({ phase: "idle" });
  const started = useRef(false);
  const mounted = useRef(false);
  const tabRef = useRef(pendingTab);
  const prefsRef = useRef(prefs);
  useEffect(() => {
    tabRef.current = pendingTab;
    prefsRef.current = prefs;
  });

  const id = d?.id;
  const authorized = d?.status === "autorizada";
  const hasDanfe = d ? filesOf(d).danfe !== undefined : false;
  const files = prepared.data;
  const failure = prepared.isError ? errorMessage(prepared.error) : null;

  useEffect(() => {
    if (!id || started.current || !authorized) return;
    if (!hasDanfe) {
      setState({ phase: "waiting_files" });
      return;
    }
    if (failure !== null) {
      started.current = true;
      const tab = tabRef.current;
      if (tab && !tab.closed) {
        renderTab(tab, {
          title: "NF-e autorizada — DANFE indisponível agora",
          detail: `Não foi possível obter o DANFE (${failure}). Volte à guia do CaféGestor e use “Visualizar DANFE”.`,
          tone: "warn",
        });
      }
      setState({ phase: "error", message: failure });
      return;
    }
    if (!files) {
      setState({ phase: "working" });
      return;
    }
    started.current = true;
    const want = prefsRef.current;
    const tab = tabRef.current;
    if (alreadyDelivered(id) || (!want.openDanfe && !want.download)) {
      closeTab(tab);
      setState({ phase: "idle" });
      return;
    }
    markDelivered(id);
    let opened: boolean | null = null;
    if (want.openDanfe && files.danfe?.kind === "danfe_pdf") {
      opened = showPdf(files.danfe.blob, tab);
    } else if (want.openDanfe && tab && !tab.closed) {
      // sem PDF para mostrar: a guia explica em vez de ficar parada
      renderTab(tab, {
        title: "NF-e autorizada",
        detail:
          files.problems[0] ?? "O DANFE desta nota não é um PDF: baixe-o pela guia do CaféGestor.",
        tone: files.problems.length > 0 ? "warn" : "ok",
      });
      opened = false;
    } else {
      closeTab(tab);
    }
    let downloaded: string | null = null;
    if (want.download) {
      const one = files.zip ?? files.danfe ?? files.xml;
      if (one) {
        saveBlob(one.blob, one.name);
        downloaded = one.name;
      }
    }
    setState({ phase: "done", opened, downloaded });
  }, [id, authorized, hasDanfe, files, failure]);

  // saiu da tela antes da autorização: a guia de espera não fica órfã (o adiamento tolera a
  // montagem dupla do modo de desenvolvimento do React)
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      setTimeout(() => {
        if (!mounted.current && !started.current) closeTab(tabRef.current);
      }, 0);
    };
  }, []);

  return state;
}
